import React, { useState , useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Briefcase } from 'lucide-react';
import { LakshLogo } from '@/components/LakshLogo';
import axios from 'axios';
import { getErrorMessage } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CALoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('laksh_ca_token')) navigate('/ca/dashboard', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ca/auth/login`, { email, password });
      localStorage.setItem('laksh_ca_token', response.data.token);
      localStorage.setItem('laksh_ca_user', JSON.stringify(response.data.user));
      localStorage.setItem('laksh_ca_firm', JSON.stringify(response.data.firm));
      toast.success('Welcome back!');
      navigate('/ca/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06100A] flex">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link data-testid="ca-login-back-home" to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
            
            <div className="mb-6">
              <LakshLogo height={40} />
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1D9E75]" />
              </div>
              <span className="text-[#1D9E75] font-semibold">CA Partner Portal</span>
            </div>
            
            <h1 className="text-3xl font-bold text-white font-['Outfit']">
              Welcome back, Partner
            </h1>
            <p className="text-gray-400 mt-2">
              Sign in to manage your clients
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="ca-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@cafirm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0a1a12] border-white/20 text-white"
                required
                data-testid="ca-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0a1a12] border-white/20 text-white"
                  required
                  data-testid="ca-login-password"
                />
                <button
                  data-testid="ca-toggle-password"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white font-semibold"
              data-testid="ca-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign in to CA Portal'}
            </Button>
          </form>

          <p className="text-center text-gray-400">
            Not a partner yet?{' '}
            <Link data-testid="ca-login-signup-link" to="/ca/signup" className="text-[#1D9E75] hover:text-[#1D9E75]/80 font-medium">
              Apply to become a CA Partner
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1D9E75] to-[#1D9E75]/70 items-center justify-center p-12">
        <div className="text-white text-center max-w-lg">
          <h2 className="text-4xl font-bold font-['Outfit'] mb-6">
            CA Partner Programme
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Earn ₹4,000+/month passive income. Manage all your clients from one dashboard.
          </p>
          <div className="space-y-4 text-left">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">✓</div>
              <span>20% recurring revenue share</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">✓</div>
              <span>Unified client compliance calendar</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">✓</div>
              <span>One-click GST filing for all clients</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
